import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Loader2, StickyNote, Plus, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pick one of the user's saved notes (commentary excerpts, study notes) and
 * insert its text into the targeted sermon outline point.
 */
const NotesPickerDialog = ({ open, onOpenChange, pointTitle = '', onInsert }) => {
  const { t } = useLanguage();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setQuery('');
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/user/notes`, { withCredentials: true });
      const list = (res.data?.notes || []).slice().reverse(); // newest first
      setNotes(list);
    } catch (err) {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = notes.filter((n) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (n.note_text || '').toLowerCase().includes(q) || (n.verse_reference || '').toLowerCase().includes(q);
  });

  const handleInsert = (note) => {
    onInsert?.(note.note_text || '');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="notes-picker-dialog">
        <DialogHeader>
          <DialogTitle>{t('sermon.insertFromNotesTitle')}</DialogTitle>
          <DialogDescription>
            {pointTitle
              ? t('sermon.willAddToPoint', { title: pointTitle })
              : t('sermon.pickNote')}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="notes-picker-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('sermon.searchNotes')}
            className="pl-8"
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
          {loading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground" data-testid="notes-picker-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {t('sermon.loading')}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground" data-testid="notes-picker-empty">
              <StickyNote className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">
                {query.trim()
                  ? t('sermon.noNotesMatch')
                  : t('sermon.noSavedNotes')}
              </p>
            </div>
          )}

          {!loading && filtered.map((note) => (
            <div
              key={note.note_id}
              data-testid={`notes-picker-item-${note.note_id}`}
              className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-800/60"
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                {note.verse_reference && (
                  <Badge variant="outline" className="text-[10px]">{note.verse_reference}</Badge>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 ml-auto text-purple-600 hover:text-purple-800 hover:bg-purple-100 dark:text-purple-300 dark:hover:bg-purple-900/40"
                  data-testid={`notes-picker-insert-${note.note_id}`}
                  onClick={() => handleInsert(note)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t('sermon.insert')}
                </Button>
              </div>
              <p className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-line line-clamp-4">
                {note.note_text}
              </p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotesPickerDialog;
